import { DESKTOP_MODE } from '@components/previewUI/utils/constants.js';
import { DEFAULT_THEME_TOKENS } from '@components/previewUI/utils/designTokens.js';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { ANIMATION_TYPES } from '@views/AdminPanelView/components/AdminPanel/components/LinkInBio/components/Design/components/BackgroundType/components/BackgroundAnimation/utils/constants.js';

export const designSlice = createSlice({
  name: 'design',
  initialState: {
    background: {
      type: 1,
      color: '#ffffff',
      image: '',
      animation: '',
      animation3dEmoji: '',
      animation3dPalette: '',
      animation3dPattern: '',
      animation3dTheme: '',
      animationType: ANIMATION_TYPES.gifs,
      video: '',
      videoSound: false,
      blur: 0,
      brightness: 0,
      opacity: 0,
      effect: '',

      isGradient: false,
      gradientColor1: '#ffffff',
      gradientColor2: '#000000',
      gradientDirection: 0,
      gradientOffset: 0,
    },

    desktopMode: DESKTOP_MODE.mobile,
    desktopModeWidth: 0,

    font: 'Open Sans',
    // Per-role typography (added 2026-07-05). Heading title / subtitle / body
    // fonts. Fall back to `font` (legacy single global font) when unset so old
    // profiles look identical until the user picks distinct fonts.
    headingFont: 'Open Sans',
    subtitleFont: 'Open Sans',
    bodyFont: 'Open Sans',
    previewBackgroundColor: '#2d2c31',
    previewProfileImage: '',
    pageNavigation: {
      backgroundColor: '#ffffff',
      opacity: 0,
      textColor: '#000000',
      view: 'top',
      // Background source of the hamburger menu panel: 1 = color (legacy),
      // 2 = gradient, 3 = image. Gradient/image fields are ignored unless picked.
      backgroundType: 1,
      gradientColor1: '#ffffff',
      gradientColor2: '#000000',
      gradientDirection: 180,
      image: '',
      fontSize: 16,
    },
    tabs: {
      activeBackgroundColor: '#ffffff',
      activeTextColor: '#000000',
      backgroundColor: '#a69c9b',
      textColor: '#ffffff',
      opacity: 0,
      // null = not set → previewUI applies its glass-style blur fallback (12/8).
      // An explicit 0 disables the blur; other numbers set it directly.
      blur: null,
      // Glass backdrop brightness/contrast (%). null = neutral (100).
      brightness: null,
      contrast: null,
      // Solid style: solid color vs two-stop gradient for the active pill.
      isGradient: false,
      gradientColor1: '#000000',
      gradientColor2: '#ffffff',
      gradientDirection: 90,
      Radius: 0,
      height: 0,
      marginTop: null,
      marginBottom: null,
      view: 'plain',
      isNavigation: true,
      isContent: true,
      isPosts: false,
      isStore: false,
      items: [],
    },
    posts: {
      backgroundColor: '#ffffff',
      textColor: '#000000',
      opacity: 0,
    },
    // Component-level brand colors generated server-side from the 4-5 base
    // colors. previewUI reads these (with safe fallbacks) so Cart / Subscribe /
    // Cta / Social / Heading / Footer stop hard-coding hex values.
    brandColors: {
      cartButtonBackgroundColor: '',
      cartButtonTextColor: '',
      subscribeButtonBackgroundColor: '',
      subscribeButtonTextColor: '',
      ctaButtonBackgroundColor: '',
      ctaButtonTextColor: '',
      socialBackgroundColor: '',
      socialColor: '',
      headingTextColor: '',
      footerBackgroundColor: '',
      footerTextColor: '',
      accentColor: '',
      primaryColor: '',
      secondaryColor: '',
      textColor: '',
      backgroundColor: '',
    },
    templateId: 0,
    themeId: 0,
    paletteId: 0,
    storeLayout: 'single',
    themeMode: 'light',
    // Theme tokens (P0 Design Import, 2026-07-08). Preset ids for typography
    // scale / density / corners / shadow; resolved to CSS vars in previewUI.
    // null = "not chosen" → previewUI emits no --ul-* vars and blocks keep their
    // own hard-coded defaults, so existing profiles render unchanged.
    themeTokens: null,
    // Global custom CSS (Pro). Raw source as typed; previewUI scopes it to the
    // site container before injecting. '' = nothing injected.
    customCss: '',
    // Uploaded custom font (Pro). Family name + CDN url; previewUI emits the
    // @font-face rule. '' = no custom font, pickers show the catalogue only.
    customFontName: '',
    customFontUrl: '',
  },
  reducers: {
    updateThemeMode: (state, action) => {
      state.themeMode = action.payload;
    },
    designStore: (state, { payload }) => {
      state.background = {
        type: payload.background.type ?? 1,
        color: payload.background.color ?? '#ffffff',
        image: payload.background.image ?? '',
        animation: payload.background.animation ?? '',
        animation3dEmoji: payload.background.animation3dEmoji ?? '😃',
        animation3dPalette: payload.background.animation3dPalette ?? '',
        animation3dPattern: payload.background.animation3dPattern ?? '',
        animation3dTheme: payload.background.animation3dTheme ?? '',
        animationType: payload.background.animationType ?? ANIMATION_TYPES.gifs,
        video: payload.background.video ?? '',
        videoSound: payload.background.videoSound ?? false,
        blur: payload.background.blur ?? 0,
        brightness: payload.background.brightness ?? 0,
        opacity: payload.background.opacity ?? 0,
        effect: payload.background.effect ?? '',

        isGradient: payload.background.isGradient ?? false,
        gradientColor1: payload.background.gradientColor1 ?? '#ffffff',
        gradientColor2: payload.background.gradientColor2 ?? '#000000',
        gradientDirection: payload.background.gradientDirection ?? 0,
        gradientOffset: payload.background.gradientOffset ?? 0,
      };

      const rawMode = payload.desktopMode ?? DESKTOP_MODE.classic;
      state.desktopMode = rawMode === 'desktop' ? DESKTOP_MODE.social : rawMode === 'mobile' ? DESKTOP_MODE.classic : rawMode;
      state.desktopModeWidth = payload.desktopModeWidth ?? 0;

      state.font = payload.font ?? 'Open Sans';
      // Role fonts fall back to the legacy single font when backend hasn't set them.
      state.headingFont = payload.headingFont ?? state.font;
      state.subtitleFont = payload.subtitleFont ?? state.font;
      state.bodyFont = payload.bodyFont ?? state.font;
      state.previewBackgroundColor = payload.previewBackgroundColor ?? '#2d2c31';
      state.previewProfileImage = payload.previewProfileImage ?? '';
      state.pageNavigation = {
        backgroundColor: payload.pageNavigation.backgroundColor ?? '#ffffff',
        opacity: payload.pageNavigation.opacity ?? 0,
        textColor: payload.pageNavigation.textColor ?? '#000000',
        view: payload.pageNavigation.view ?? 'top',
        backgroundType: payload.pageNavigation.backgroundType ?? 1,
        gradientColor1: payload.pageNavigation.gradientColor1 || payload.pageNavigation.backgroundColor || '#ffffff',
        gradientColor2: payload.pageNavigation.gradientColor2 || '#000000',
        gradientDirection: payload.pageNavigation.gradientDirection ?? 180,
        image: payload.pageNavigation.image ?? '',
        fontSize: payload.pageNavigation.fontSize ?? 16,
      };
      state.tabs = {
        activeBackgroundColor: payload.tabs.activeBackgroundColor ?? '#ffffff',
        activeTextColor: payload.tabs?.activeTextColor ?? '#000000',
        backgroundColor: payload.tabs.backgroundColor ?? '#a69c9b',
        textColor: payload.tabs.textColor ?? '#ffffff',
        opacity: payload.tabs?.opacity ?? 0,
        blur: payload.tabs?.blur ?? null,
        brightness: payload.tabs?.brightness ?? null,
        contrast: payload.tabs?.contrast ?? null,
        isGradient: payload.tabs?.isGradient ?? false,
        gradientColor1: payload.tabs?.gradientColor1 ?? '#000000',
        gradientColor2: payload.tabs?.gradientColor2 ?? '#ffffff',
        gradientDirection: payload.tabs?.gradientDirection ?? 90,
        Radius: payload.tabs?.radius ?? 0,
        height: payload.tabs?.height ?? 0,
        // null = not set → previewUI keeps legacy spacing (top 6 / bottom 20). Explicit 0 is honored.
        marginTop: payload.tabs?.marginTop ?? null,
        marginBottom: payload.tabs?.marginBottom ?? null,
        view: payload.tabs?.view ?? 'plain',
        isNavigation: payload.tabs?.isNavigation ?? true,
        isContent: payload.tabs?.isContent ?? true,
        isPosts: payload.tabs?.isPosts ?? true,
        isStore: payload.tabs?.isStore ?? true,
        contentN: payload.tabs?.contentN ?? 1,
        postsN: payload.tabs?.postsN ?? 2,
        storeN: payload.tabs?.storeN ?? 3,
        items: payload.tabs.items ?? [],
      };
      state.posts = {
        backgroundColor: payload.posts.backgroundColor ?? '#a69c9b',
        textColor: payload.posts.textColor ?? '#ffffff',
        opacity: payload.posts.opacity ?? 0,
      };
      // Empty string when backend hasn't generated a value yet — previewUI
      // treats falsy as "use my hard-coded default" so legacy profiles stay
      // visually unchanged until the user re-applies brand colors / a template.
      const bc = payload.brandColors ?? {};
      state.brandColors = {
        cartButtonBackgroundColor: bc.cartButtonBackgroundColor ?? '',
        cartButtonTextColor: bc.cartButtonTextColor ?? '',
        subscribeButtonBackgroundColor: bc.subscribeButtonBackgroundColor ?? '',
        subscribeButtonTextColor: bc.subscribeButtonTextColor ?? '',
        ctaButtonBackgroundColor: bc.ctaButtonBackgroundColor ?? '',
        ctaButtonTextColor: bc.ctaButtonTextColor ?? '',
        socialBackgroundColor: bc.socialBackgroundColor ?? '',
        socialColor: bc.socialColor ?? '',
        headingTextColor: bc.headingTextColor ?? '',
        footerBackgroundColor: bc.footerBackgroundColor ?? '',
        footerTextColor: bc.footerTextColor ?? '',
        // Base palette roles (resolved server-side) — the source for the semantic
        // colour tokens (--ul-accent / -primary / -text / -bg / -surface).
        accentColor: bc.accentColor ?? '',
        primaryColor: bc.primaryColor ?? '',
        secondaryColor: bc.secondaryColor ?? '',
        textColor: bc.textColor ?? '',
        backgroundColor: bc.backgroundColor ?? '',
      };
      state.storeLayout = payload.storeLayout ?? 'single';
      state.isHideLogo = payload.isHideLogo ?? false;
      // Preserve if the backend ever persists it; otherwise stay null (unset).
      state.themeTokens = payload.themeTokens ?? null;
      state.customCss = payload.customCss ?? '';
      state.customFontName = payload.customFontName ?? '';
      state.customFontUrl = payload.customFontUrl ?? '';
    },
    designStoreId: (state, { payload }) => {
      state.templateId = payload.templateId ?? 0;
      state.themeId = payload.themeId ?? 0;
      state.paletteId = payload.paletteId ?? 0;
    },
    updateBackground: (state, action) => {
      state.background = action.payload;
    },
    updateDesktopMode: (state, action) => {
      state.desktopMode = action.payload;
    },
    updateDesktopModeWidth: (state, action) => {
      state.desktopModeWidth = action.payload;
    },
    updateFont: (state, action) => {
      state.font = action.payload;
    },
    updateCustomCss: (state, action) => {
      state.customCss = action.payload;
    },
    updateCustomFont: (state, action) => {
      state.customFontName = action.payload?.customFontName ?? '';
      state.customFontUrl = action.payload?.customFontUrl ?? '';
    },
    updateHeadingFont: (state, action) => {
      state.headingFont = action.payload;
    },
    updateSubtitleFont: (state, action) => {
      state.subtitleFont = action.payload;
    },
    updateBodyFont: (state, action) => {
      state.bodyFont = action.payload;
    },
    updatePreviewBackgroundColor: (state, action) => {
      state.previewBackgroundColor = action.payload;
    },
    updatePreviewProfileImage: (state, action) => {
      state.previewProfileImage = action.payload;
    },
    updatePageNavigation: (state, action) => {
      state.pageNavigation = action.payload;
    },
    updateTabs: (state, action) => {
      state.tabs = action.payload;
    },
    /**
     * Append a newly-created custom tab into tabs.items.
     * Backend returns the tab with either PascalCase (direct DbML serialization) or camelCase.
     * We normalize to the shape used by TabDragDrop/Navigation (camelCase-ish matching predefined tabs).
     */
    addCustomTab: (state, { payload }) => {
      if (!payload) return;
      const normalized = {
        id: payload.id ?? payload.Id,
        type: payload.type ?? payload.Type ?? 'custom',
        blockType: payload.blockType ?? payload.BlockType,
        customKey: payload.customKey ?? payload.CustomKey,
        title: payload.title ?? payload.Title,
        icon: payload.icon ?? payload.Icon,
        description: payload.description ?? payload.Description,
        settings: payload.settings ?? payload.Settings,
        isActive: payload.isActive ?? payload.IsActive ?? true,
        isAdded: payload.isAdded ?? payload.IsAdded ?? true,
        n: payload.n ?? payload.N ?? (state.tabs.items?.length ?? 0) + 1,
      };
      if (!state.tabs.items) state.tabs.items = [];
      state.tabs.items.push(normalized);
    },
    /** Remove a custom tab (by id) from local state. Call after DELETE succeeds. */
    removeCustomTab: (state, { payload: tabId }) => {
      if (!state.tabs.items) return;
      state.tabs.items = state.tabs.items.filter((t) => (t.id ?? t.Id) !== tabId);
    },
    /** Merge updates (rename, icon change, reorder) into an existing custom tab. */
    updateCustomTab: (state, { payload }) => {
      if (!payload) return;
      const targetId = payload.id ?? payload.Id;
      if (!targetId || !state.tabs.items) return;
      state.tabs.items = state.tabs.items.map((t) => {
        const tid = t.id ?? t.Id;
        return tid === targetId ? { ...t, ...payload } : t;
      });
    },
    updatePostsDesign: (state, action) => {
      state.posts = action.payload;
    },
    updateTemplateId: (state, action) => {
      state.templateId = action.payload;
    },
    updateThemeId: (state, action) => {
      state.themeId = action.payload;
    },
    updatePaletteId: (state, action) => {
      state.paletteId = action.payload;
    },
    designUpdateStoreLayout: (state, action) => {
      state.storeLayout = action.payload;
    },
    updateIsHideLogo: (state, action) => {
      state.isHideLogo = action.payload;
    },
    // Merge a partial theme-token change (e.g. { radius: 'sharp' }) onto the
    // current selection, seeding sensible defaults on first pick so a single
    // control doesn't leave the other roles undefined.
    updateThemeTokens: (state, action) => {
      state.themeTokens = { ...DEFAULT_THEME_TOKENS, ...state.themeTokens, ...action.payload };
    },
    // Wholesale replace of the entire design state. Used by the non-destructive
    // template live-preview to snapshot the current design and restore it exactly
    // on exit (designStore maps API-shaped payloads and can't round-trip the store).
    designRestore: (_state, { payload }) => payload,
  },
});

export const {
  designStore,
  designStoreId,
  updateBackground,
  updateDesktopMode,
  updateDesktopModeWidth,
  updateFont,
  updateHeadingFont,
  updateSubtitleFont,
  updateBodyFont,
  updatePreviewBackgroundColor,
  updatePreviewProfileImage,
  updatePageNavigation,
  updateTabs,
  addCustomTab,
  removeCustomTab,
  updateCustomTab,
  updatePostsDesign,
  updateTemplateId,
  updateThemeId,
  updatePaletteId,
  designUpdateStoreLayout,
  updateIsHideLogo,
  updateThemeMode,
  updateThemeTokens,
  updateCustomCss,
  updateCustomFont,
  designRestore,
} = designSlice.actions;

export const selectDesign = (state) => state.design;
export const selectBackground = (state) => state.design.background;
export const selectDesktopMode = (state) => state.design.desktopMode;
export const selectDesktopModeWidth = (state) => state.design.desktopModeWidth;
export const selectFont = (state) => state.design.font;
export const selectHeadingFont = (state) => state.design.headingFont ?? state.design.font;
export const selectSubtitleFont = (state) => state.design.subtitleFont ?? state.design.font;
export const selectBodyFont = (state) => state.design.bodyFont ?? state.design.font;
export const selectTypography = createSelector([selectHeadingFont, selectSubtitleFont, selectBodyFont], (headingFont, subtitleFont, bodyFont) => ({
  headingFont,
  subtitleFont,
  bodyFont,
}));
export const selectPreviewBackgroundColor = (state) => state.design.previewBackgroundColor;
export const selectPreviewProfileImage = (state) => state.design.previewProfileImage;
export const selectPageNavigation = (state) => state.design.pageNavigation;
export const selectTabs = (state) => state.design.tabs;
const selectTabsItems = (state) => state.design.tabs?.items;

export const selectCustomTabs = createSelector(selectTabsItems, (items) => (items || []).filter((t) => (t.type ?? t.Type) === 'custom'));

export const selectCustomTabByKey = createSelector([selectTabsItems, (_state, customKey) => customKey], (items, customKey) =>
  (items || []).find((t) => (t.type ?? t.Type) === 'custom' && (t.customKey ?? t.CustomKey) === customKey),
);
export const selectPostsDesign = (state) => state.design.posts;
export const selectTemplateId = (state) => state.design.templateId;
export const selectThemeId = (state) => state.design.themeId;
export const selectPaletteId = (state) => state.design.paletteId;

export const selectStoreLayout = (state) => state.design.storeLayout;
export const selectIsHideLogo = (state) => state.design.isHideLogo;
export const selectBrandColors = (state) => state.design.brandColors;
export const selectThemeMode = (state) => state.design.themeMode;
export const selectThemeTokens = (state) => state.design.themeTokens;
export const selectCustomCss = (state) => state.design.customCss ?? '';
export const selectCustomFontName = (state) => state.design.customFontName ?? '';
export const selectCustomFontUrl = (state) => state.design.customFontUrl ?? '';
export const selectCustomFont = createSelector([selectCustomFontName, selectCustomFontUrl], (customFontName, customFontUrl) => ({ customFontName, customFontUrl }));
export default designSlice.reducer;
